import { create } from "zustand";
import { supabase } from "../lib/supabase";
import type { User } from "@supabase/supabase-js";

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds
const REQUEST_TIMEOUT = 900000; // 15 minutes

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  error: null,
  initialized: false,

  checkAuth: async () => {
    // Skip if already initialized and has user
    const state = useAuthStore.getState();
    if (state.initialized) {
      return;
    }

    let retryCount = 0;
    let lastError: Error | null = null;

    while (retryCount < MAX_RETRIES) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      try {
        set({ loading: true, error: null });

        console.log("Checking auth state...", {
          attempt: retryCount + 1,
          timestamp: new Date().toISOString(),
        });

        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error("Session error:", {
            error: sessionError,
            attempt: retryCount + 1,
          });
          throw sessionError;
        }

        console.log("Session check result:", {
          hasSession: !!session,
          expiresAt: session?.expires_at,
          timestamp: new Date().toISOString(),
        });

        if (!session) {
          console.log("No active session found");
          set({ user: null, loading: false, initialized: true });
          return;
        }

        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();

        if (userError) {
          console.error("User fetch error:", {
            error: userError,
            attempt: retryCount + 1,
          });
          throw userError;
        }

        if (!user) {
          console.log("No user data found in session");
          set({ user: null, loading: false, initialized: true });
          return;
        }

        const isAdmin = user.app_metadata?.role === "admin";
        console.log("User role check:", {
          isAdmin,
          metadata: user.app_metadata,
          timestamp: new Date().toISOString(),
          email: user.email,
        });

        // Allow any authenticated user to access prompt admin
        // We'll keep the admin check for other admin areas

        console.log("Auth check successful:", {
          userId: user.id,
          email: user.email,
          timestamp: new Date().toISOString(),
        });

        set({ user, loading: false, error: null, initialized: true });
        return; // Success - exit retry loop
      } catch (error) {
        lastError = error as Error;
        console.warn(`Auth check attempt ${retryCount + 1} failed:`, error);

        if (error instanceof Error && error.name === "AbortError") {
          console.error("Auth check timeout:", {
            attempt: retryCount + 1,
            timeout: REQUEST_TIMEOUT,
          });
          set({
            user: null,
            error:
              "Connection timed out. Please check your internet connection and try again.",
            loading: false,
            initialized: true,
          });
          break;
        }

        if (retryCount < MAX_RETRIES - 1) {
          console.log(`Retrying auth check in ${RETRY_DELAY}ms...`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        }
        retryCount++;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    if (retryCount === MAX_RETRIES) {
      console.error("Auth check failed after max retries:", {
        attempts: MAX_RETRIES,
        finalError: lastError,
      });
      set({
        user: null,
        error:
          lastError?.message || "Authentication failed after multiple attempts",
        loading: false,
        initialized: true,
      });

      // Clean up any invalid session
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.error("Error during sign out:", signOutError);
      }
    }
  },

  signIn: async (email: string, password: string) => {
    let retryCount = 0;
    let lastError: Error | null = null;

    while (retryCount < MAX_RETRIES) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      try {
        set({ loading: true, error: null });

        console.log("Attempting sign in:", {
          email,
          attempt: retryCount + 1,
          timestamp: new Date().toISOString(),
        });

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          console.error("Sign in error:", {
            error,
            attempt: retryCount + 1,
          });
          throw error;
        }

        if (!data.user) {
          console.error("No user data returned from sign in");
          throw new Error("No user data returned");
        }

        const isAdmin = data.user.app_metadata?.role === "admin";
        console.log("User role check:", {
          isAdmin,
          metadata: data.user.app_metadata,
        });

        if (!isAdmin) {
          console.warn("Non-admin user attempted sign in");
          await supabase.auth.signOut();
          throw new Error("Access denied. Admin privileges required.");
        }

        console.log("Sign in successful:", {
          userId: data.user.id,
          email: data.user.email,
          timestamp: new Date().toISOString(),
        });

        set({
          user: data.user,
          loading: false,
          error: null,
          initialized: true,
        });
        return; // Success - exit retry loop
      } catch (error) {
        lastError = error as Error;
        console.warn(`Sign in attempt ${retryCount + 1} failed:`, error);

        if (error instanceof Error && error.name === "AbortError") {
          console.error("Sign in timeout:", {
            attempt: retryCount + 1,
            timeout: REQUEST_TIMEOUT,
          });
          set({
            user: null,
            error: "Connection timed out. Please try again.",
            loading: false,
            initialized: true,
          });
          break;
        }

        if (retryCount < MAX_RETRIES - 1) {
          console.log(`Retrying sign in in ${RETRY_DELAY}ms...`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        }
        retryCount++;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    if (retryCount === MAX_RETRIES) {
      console.error("Sign in failed after max retries:", {
        attempts: MAX_RETRIES,
        finalError: lastError,
      });
      set({
        user: null,
        error: lastError?.message || "Sign in failed after multiple attempts",
        loading: false,
        initialized: true,
      });
    }
  },

  signOut: async () => {
    let retryCount = 0;
    let lastError: Error | null = null;

    while (retryCount < MAX_RETRIES) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      try {
        set({ loading: true, error: null });
        console.log("Attempting sign out...", {
          attempt: retryCount + 1,
          timestamp: new Date().toISOString(),
        });

        await supabase.auth.signOut();
        console.log("Sign out successful");
        set({ user: null, loading: false, error: null, initialized: true });
        return; // Success - exit retry loop
      } catch (error) {
        lastError = error as Error;
        console.warn(`Sign out attempt ${retryCount + 1} failed:`, error);

        if (error instanceof Error && error.name === "AbortError") {
          console.error("Sign out timeout:", {
            attempt: retryCount + 1,
            timeout: REQUEST_TIMEOUT,
          });
          set({
            error: "Sign out timed out. Please try again.",
            loading: false,
            initialized: true,
          });
          break;
        }

        if (retryCount < MAX_RETRIES - 1) {
          console.log(`Retrying sign out in ${RETRY_DELAY}ms...`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        }
        retryCount++;
      } finally {
        clearTimeout(timeoutId);
      }
    }

    if (retryCount === MAX_RETRIES) {
      console.error("Sign out failed after max retries:", {
        attempts: MAX_RETRIES,
        finalError: lastError,
      });
      set({
        error: lastError?.message || "Sign out failed after multiple attempts",
        loading: false,
        initialized: true,
      });
    }
  },
}));
